// src/components/sales/sections/OffersSection.jsx
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Lightbulb, Package, TrendingUp, AlertCircle, RefreshCw, Target } from 'lucide-react';

// ── Offer-type → brand color map ─────────────────────────────────────────────
const OFFER_COLOR_MAP = {
  mn3k:       '#ed145b', // pink-500
  mm5k:       '#0093cf', // brightblue-500
  mm7k:       '#009391', // teal-500
  ws15k:      '#ffdd00', // yellow-500
  ws20k:      '#92278f', // violet-500
  ws25k:      '#f58025', // brand-500
  basic:      '#0093cf',
  premium:    '#009391',
  enterprise: '#92278f',
  custom:     '#f58025',
};
const FALLBACK_COLORS = ['#0093cf','#009391','#ffdd00','#ed145b','#92278f','#f58025'];
const getOfferTypeColor = (type, idx=0) => OFFER_COLOR_MAP[type?.toLowerCase()] || FALLBACK_COLORS[idx % 6];

const OffersSection = ({ data, loading, error, isEmpty }) => {
  const cardCls = 'bg-white rounded-xl shadow-sm border border-grey-200 p-6';

  if (loading) return (
    <div className={cardCls}>
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><Package className="h-4 w-4" />Offers Overview</h3>
      <div className="flex items-center justify-center h-48 text-grey-400"><div className="text-center"><RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" /><p>Loading offers…</p></div></div>
    </div>
  );

  if (error) return (
    <div className={cardCls}>
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><Package className="h-4 w-4" />Offers Overview</h3>
      <div className="flex items-center justify-center h-48 text-pink-600"><div className="text-center"><AlertCircle className="h-8 w-8 mx-auto mb-2" /><p>{error}</p></div></div>
    </div>
  );

  const getOffersData = () => {
    if (!data) return [];
    if (data.by_type) return Object.entries(data.by_type).map(([type, count], i) => ({ name: type, value: count, color: getOfferTypeColor(type, i) }));
    if (Array.isArray(data)) {
      const counts = {};
      data.forEach(o => { const t = o.offer_type || o.type || 'Unknown'; counts[t] = (counts[t]||0)+1; });
      return Object.entries(counts).map(([type, count], i) => ({ name: type, value: count, color: getOfferTypeColor(type, i) }));
    }
    return [];
  };

  const offersData    = getOffersData();
  const totalOffers   = offersData.reduce((s,i) => s+i.value, 0);
  const totalSent     = data?.total_sent || totalOffers;
  const highProb      = data?.high_probability  || 0;
  const medProb       = data?.medium_probability || 0;
  const lowProb       = data?.low_probability    || 0;

  const probData = [
    { name:'High',   value: highProb, color:'#009391' }, // teal
    { name:'Medium', value: medProb,  color:'#fff786' }, // yellow
    { name:'Low',    value: lowProb,  color:'#ed145b' }, // pink
  ].filter(i => i.value > 0);

  if (totalSent===0 || isEmpty || offersData.length===0) return (
    <div className={cardCls}>
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><Package className="h-4 w-4" />Offers Overview</h3>
      <div className="text-center text-grey-400 py-8"><Package className="h-12 w-12 mx-auto mb-3 text-grey-300" /><p>No offer data available</p></div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active||!payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-grey-200 rounded-lg shadow-lg">
        <p className="text-xs font-semibold text-darkblue-500 mb-1">{label||d.name}</p>
        <p className="text-xs text-brightblue-600"><span className="font-medium">{d.value}</span> offers {totalOffers>0&&`(${((d.value/totalOffers)*100).toFixed(1)}%)`}</p>
      </div>
    );
  };

  // Summary stat cards
  const statCards = [
    { label:'Total Sent',       value: totalSent,  bg:'bg-brightblue-700' },
    { label:'High Probability', value: highProb,   bg:'bg-teal-700' },
    { label:'Medium Probability',value: medProb,   bg:'bg-yellow-700' },
    { label:'Low Probability',  value: lowProb,    bg:'bg-pink-700' },
  ];

  return (
    <div className={cardCls}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-darkblue-500 flex items-center gap-2">
          <Package className="h-4 w-4" />Offers Overview
          {totalSent>0 && offersData.length>1 && (
            <div className="relative group ml-1">
              <Lightbulb className="h-4 w-4 text-yellow-500 cursor-pointer" />
              <div className="absolute left-0 mt-2 w-72 bg-white border border-grey-200 rounded-lg shadow-lg p-3 text-xs text-darkblue-500 z-10 hidden group-hover:block space-y-1">
                {(() => {
                  const top = offersData.reduce((p,c)=>p.value>c.value?p:c);
                  return (
                    <>
                      <div>🎯 Most popular: <span className="font-semibold">{top.name}</span> ({top.value})</div>
                      <div>📊 High probability: {totalSent>0?((highProb/totalSent)*100).toFixed(1):0}%</div>
                      <div>💰 {offersData.length} offer types</div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </h3>
        <span className="text-xs text-grey-400">{offersData.length} offer type{offersData.length!==1?'s':''}</span>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {statCards.map(c => (
          <div key={c.label} className={`bg-brightblue-600 rounded-xl p-3 text-center`}>
            <div className="text-xl font-bold text-white">{c.value}</div>
            <div className="text-xs text-white/80 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {offersData.length>0 && (
          <div>
            <h4 className="text-xs font-semibold text-darkblue-500 mb-2 flex items-center gap-1"><Target className="h-3.5 w-3.5" />By Offer Type</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={offersData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#dcdeef" />
                  <XAxis dataKey="name" tick={{ fontSize:11, fill:'#152451' }} angle={-45} textAnchor="end" height={55} />
                  <YAxis tick={{ fontSize:11, fill:'#152451' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[4,4,0,0]}>
                    {offersData.map((e,i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {probData.length>0 && (
          <div>
            <h4 className="text-xs font-semibold text-darkblue-500 mb-2 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />By Probability</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={probData} cx="50%" cy="50%" innerRadius={30} outerRadius={70} dataKey="value">
                    {probData.map((e,i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 space-y-1">
              {probData.map((item,i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{backgroundColor:item.color}} /><span className="text-darkblue-500">{item.name}</span></div>
                  <span className="font-semibold text-darkblue-500">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Breakdown list */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-darkblue-500 mb-2">Detailed Breakdown</h4>
        {[...offersData].sort((a,b)=>b.value-a.value).map((item,i) => {
          const pct = totalOffers>0 ? ((item.value/totalOffers)*100).toFixed(1) : '0';
          return (
            <div key={i} className="flex items-center justify-between p-2.5 bg-grey-50 rounded-lg">
              <div className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded" style={{backgroundColor:item.color}} /><span className="text-xs font-medium text-darkblue-500">{item.name}</span></div>
              <div className="text-xs"><span className="font-semibold text-darkblue-500">{item.value}</span><span className="text-grey-400 ml-1">({pct}%)</span></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OffersSection;